import {
  checkEmailAddress as checkEmail,
  login as loginUser,
  forgotPassword as userForgotPassword,
  resetPassword as resetPasswordAPI,
  updatePassword as updatePasswordAPI,
  registerWithToken as registerWithTokenApi,
  getTerms as getTermsAPI,
  postOnboarding as postOnboardingAPI
} from '../api/sessions'
import {
  IForgotPasswordResponse,
  ILoginFailedResponse,
  ILoginSuccessResponse
} from '../api/types/sessions'
import { clearErrorBoundary } from '../events/errorEventEmitter'

import { getEmail, getUser } from '../state/selectors/user'
import { setIdinResultStatus } from '../state/slices/idin_slice'
import { setRelations } from '../state/slices/relations_slice'
import { setTerms } from '../state/slices/terms_slice'
import { setShowFirstUse, setToken, setUser } from '../state/slices/user_slice'
import { setUserId, logEvent } from '../utils/firebase'
import { useAppDispatch, useAppSelector } from './state'
import useAgency from './use_agency'
import usePolicies from './use_policies'

export const useSession = () => {
  const dispatch = useAppDispatch()
  const { getPolicies } = usePolicies()
  const { getAgency } = useAgency()
  const user = useAppSelector(getUser)

  const email = useAppSelector(getEmail)

  const checkEmailAddress = async (email: string): Promise<string> => {
    const result = await checkEmail(email)

    dispatch(setIdinResultStatus(undefined))
    dispatch(setShowFirstUse(false))

    return result.data.status
  }

  const forgotPassword = async (email: string, resetPasswordUrl: string) => {
    const { data } = await userForgotPassword(
      email,
      resetPasswordUrl
    )

    // TODO: - Ask Paskal to make this endpoint return the same thing for a success as error case
    if (data as IForgotPasswordResponse) {
      return (data as IForgotPasswordResponse).result
    } else if (data as boolean) {
      return data
    } else {
      return false
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const { data } = await loginUser(email, password)
      const loginResponseSuccess = data as ILoginSuccessResponse

      // Clear error boundary state
      clearErrorBoundary()

      dispatch(setUser(loginResponseSuccess.user))
      setUserId(loginResponseSuccess.user.id)
      logEvent('login')

      dispatch(setTerms(loginResponseSuccess.terms))
      dispatch(setRelations(loginResponseSuccess.relations))
      if (loginResponseSuccess.relations.length > 0) {
        await getAgency(loginResponseSuccess.relations[0].agencyId)
      }
      getPolicies()

      return {
        success: true,
        attemptLeft: null
      }
    } catch
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error: any) {
      const response = error?.response?.data as ILoginFailedResponse | undefined

      return {
        success: false,
        data: null,
        message: response?.message || 'Er is iets misgegaan. Probeer het later opnieuw.',
        attemptLeft: response?.attemptsLeft || 0
      }
    }
  }

  const resetPassword = async (
    password: string,
    passwordRepeat: string,
    token: string
  ) => {
    await resetPasswordAPI(
      password,
      passwordRepeat,
      token
    )
  }

  const updatePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    const result = await updatePasswordAPI(
      email!,
      currentPassword,
      newPassword,
      newPassword
    )

    dispatch(setToken(result.data.token))

    return result.data
  }

  const registerWithToken = async (
    email: string,
    password: string,
    passwordRepeat: string,
    registerToken: string
  ) => {
    const result = await registerWithTokenApi(
      email,
      password,
      passwordRepeat,
      registerToken
    )

    const loginResult = await login(email, password)

    if (loginResult.success) {
      return result
    } else {
      return loginResult
    }
  }

  const getTerms = async () => {
    const { data } = await getTermsAPI()

    return data
  }

  const postOnboarding = async ({
    firstName,
    termsRead,
    relationId,
    relationNumber
  }: {
    firstName: string
    termsRead: boolean
    relationId: string
    relationNumber: string
  }) => {
    const { data } = await postOnboardingAPI({
      firstName,
      termsRead,
      relationId,
      relationNumber
    })

    dispatch(
      setUser({
        ...user!,
        firstName,
        termsRead
      })
    )

    return data
  }

  return {
    checkEmailAddress,
    login,
    forgotPassword,
    resetPassword,
    updatePassword,
    registerWithToken,
    getTerms,
    postOnboarding
  }
}

export default useSession
